import {
  BiCalendar,
  BiCheckCircle,
  BiMap,
  BiNews,
  BiShare,
  BiSolidPhotoAlbum,
  BiTrophy,
} from 'react-icons/bi';
import { FaHardDrive, FaHandshake, FaPeopleGroup } from 'react-icons/fa6';
import { MdOutlineQuiz } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaMoneyBill, FaReceipt } from 'react-icons/fa';
import { IconType } from 'react-icons';
import { SubscriptionType } from '../types/misc';

export enum Feature {
  EASY_SHARING = 'EASY_SHARING',
  MEMBER_ADMINISTRATION = 'MEMBER_ADMINISTRATION',
  EVENTS = 'EVENTS',
  PARTICIPATION_INSIGHTS = 'PARTICIPATION_INSIGHTS',
  TASKS = 'TASKS',
  STATS_AND_SCORES = 'STATS_AND_SCORES',
  COMMUNICATION = 'COMMUNICATION',
  CHATS = 'CHATS',
  COST_SPLITTING = 'COST_SPLITTING',
  FINANCIAL_ADMINISTRATION = 'FINANCIAL_ADMINISTRATION',
  SALES = 'SALES',
  MARKETPLACE = 'MARKETPLACE',
  PHOTO_ALBUMS = 'PHOTO_ALBUMS',
  LOCATIONS = 'LOCATIONS',
  FILES = 'FILES',
  FORMS = 'FORMS',
  SCHEDULES = 'SCHEDULES',
  WALL = 'WALL',
  SPONSORS = 'SPONSORS',
}

export type CommunityFeatures = {
  [K in Feature]?: boolean;
};

export interface FeatureConfig {
  key: Feature;
  icon: IconType;
  title: string;
  description: string;
  group: string;
  betaFeatureKey?: string;
  businessOnly?: boolean;
}

export type BusinessFeatures = {
  MEMBER_ADMINISTRATION?: boolean;
  PARTICIPATION_INSIGHTS?: boolean;
  COMMUNICATION?: boolean;
  FINANCIAL_ADMINISTRATION?: boolean;
  EVENTS?: boolean;
  PHOTO_ALBUMS?: boolean;
  SPONSORS?: boolean;
};

export function getBusinessSettingsFeatures(business?: {
  community?: { features?: CommunityFeatures };
  dashboard?: { features?: BusinessFeatures };
}): CommunityFeatures | BusinessFeatures {
  return business?.community?.features ?? business?.dashboard?.features ?? {};
}

export const useFeatureList = (): FeatureConfig[] => {
  const { t } = useTranslation();
  return [
    {
      key: Feature.EASY_SHARING,
      icon: BiShare,
      title: t('page.onboarding.communityFeaturesScreen.features.easySharing.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.easySharing.description'),
      group: 'sharing',
    },
    {
      key: Feature.MEMBER_ADMINISTRATION,
      icon: FaPeopleGroup,
      title: t('page.onboarding.communityFeaturesScreen.features.memberAdministration.title'),
      description: t(
        'page.onboarding.communityFeaturesScreen.features.memberAdministration.description',
      ),
      group: 'sharing',
    },
    {
      key: Feature.EVENTS,
      icon: BiCalendar,
      title: t('page.onboarding.communityFeaturesScreen.features.events.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.events.description'),
      group: 'organization',
    },
    {
      key: Feature.PARTICIPATION_INSIGHTS,
      icon: BiTrophy,
      title: t('page.onboarding.communityFeaturesScreen.features.participationInsights.title'),
      description: t(
        'page.onboarding.communityFeaturesScreen.features.participationInsights.description',
      ),
      group: 'organization',
    },
    {
      key: Feature.TASKS,
      icon: BiCheckCircle,
      title: t('page.onboarding.communityFeaturesScreen.features.tasks.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.tasks.description'),
      group: 'organization',
    },
    {
      key: Feature.STATS_AND_SCORES,
      icon: BiTrophy,
      title: t('page.onboarding.communityFeaturesScreen.features.statsAndScores.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.statsAndScores.description'),
      group: 'organization',
    },
    {
      key: Feature.COMMUNICATION,
      icon: FaEnvelope,
      title: t('page.onboarding.communityFeaturesScreen.features.communication.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.communication.description'),
      group: 'communication',
    },
    {
      key: Feature.COST_SPLITTING,
      icon: FaReceipt,
      title: t('page.onboarding.communityFeaturesScreen.features.costSplitting.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.costSplitting.description'),
      group: 'financial',
    },
    {
      key: Feature.FINANCIAL_ADMINISTRATION,
      betaFeatureKey: 'FINANCIAL_ADMINISTRATION',
      icon: FaMoneyBill,
      title: t('page.onboarding.communityFeaturesScreen.features.financialAdministration.title'),
      description: t(
        'page.onboarding.communityFeaturesScreen.features.financialAdministration.description',
      ),
      group: 'financial',
    },
    {
      key: Feature.SALES,
      betaFeatureKey: 'SALES',
      icon: FaMoneyBill,
      title: t('page.onboarding.communityFeaturesScreen.features.sales.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.sales.description'),
      group: 'financial',
    },
    {
      key: Feature.PHOTO_ALBUMS,
      icon: BiSolidPhotoAlbum,
      title: t('page.onboarding.communityFeaturesScreen.features.photoAlbums.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.photoAlbums.description'),
      group: 'media',
    },
    {
      key: Feature.LOCATIONS,
      betaFeatureKey: 'LOCATIONS',
      icon: BiMap,
      title: t('page.onboarding.communityFeaturesScreen.features.locations.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.locations.description'),
      group: 'community',
    },
    {
      key: Feature.FORMS,
      icon: MdOutlineQuiz,
      title: t('page.onboarding.communityFeaturesScreen.features.forms.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.forms.description'),
      group: 'organization',
    },
    {
      key: Feature.FILES,
      betaFeatureKey: 'FILES',
      icon: FaHardDrive,
      title: t('page.onboarding.communityFeaturesScreen.features.files.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.files.description'),
      group: 'media',
    },
    {
      key: Feature.SCHEDULES,
      betaFeatureKey: 'SCHEDULES',
      icon: BiCalendar,
      title: t('page.onboarding.communityFeaturesScreen.features.schedules.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.schedules.description'),
      group: 'organization',
    },
    {
      key: Feature.WALL,
      icon: BiNews,
      title: t('page.onboarding.communityFeaturesScreen.features.wall.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.wall.description'),
      group: 'communication',
    },
    {
      key: Feature.SPONSORS,
      icon: FaHandshake,
      title: t('page.onboarding.communityFeaturesScreen.features.sponsors.title'),
      description: t('page.onboarding.communityFeaturesScreen.features.sponsors.description'),
      group: 'community',
      businessOnly: true,
    },
  ];
};

const freeFeatures: CommunityFeatures = {
  [Feature.EASY_SHARING]: true,
  [Feature.EVENTS]: true,
  [Feature.TASKS]: true,
  [Feature.STATS_AND_SCORES]: true,
  [Feature.COST_SPLITTING]: true,
  [Feature.CHATS]: true,
  [Feature.MEMBER_ADMINISTRATION]: true,
  [Feature.PHOTO_ALBUMS]: true,
  [Feature.LOCATIONS]: true,
  [Feature.FILES]: true,
  [Feature.FORMS]: true,
  [Feature.SALES]: true,
  [Feature.SCHEDULES]: true,
  [Feature.WALL]: true,
};

export const featuresInSubscription: Record<SubscriptionType, CommunityFeatures> = {
  [SubscriptionType.FREE]: freeFeatures,
  [SubscriptionType.BASE]: {
    ...freeFeatures,
    [Feature.COMMUNICATION]: true,
    [Feature.PARTICIPATION_INSIGHTS]: true,
    [Feature.FINANCIAL_ADMINISTRATION]: true,
  },
  [SubscriptionType.PRO]: {
    ...freeFeatures,
    [Feature.COMMUNICATION]: true,
    [Feature.PARTICIPATION_INSIGHTS]: true,
    [Feature.FINANCIAL_ADMINISTRATION]: true,
    [Feature.FORMS]: undefined,
  },
};

export function getMinimumSubscriptionForFeatures(selectedFeatures: CommunityFeatures): SubscriptionType {
  const requiredFeatures = Object.entries(selectedFeatures)
    //eslint-disable-next-line
    .filter(([_, value]) => value)
    .map(([key]) => key as Feature);

  const needsPro = requiredFeatures.some(
    (feature) =>
      !featuresInSubscription[SubscriptionType.FREE][feature] &&
      !featuresInSubscription[SubscriptionType.BASE][feature],
  );
  if (needsPro) return SubscriptionType.PRO;

  const needsBase = requiredFeatures.some(
    (feature) => !featuresInSubscription[SubscriptionType.FREE][feature],
  );
  if (needsBase) return SubscriptionType.BASE;

  return SubscriptionType.FREE;
}
